import config from "../config.js";
import { apiFetch } from "./apiClient.js";
import { buildActionPrompt } from "./actionPrompt.js";

// '내가 할 일 찾기'(ai-prompt-export-v1) 클라이언트 유틸.
// 완성 프롬프트는 응답으로만 다루고 저장·URL·이벤트에 싣지 않는다.

// 기본 활성. 긴급 차단이 필요할 때만 REACT_APP_PROMPT_EXPORT_ENABLED=false로 끈다.
export const PROMPT_EXPORT_ENABLED =
  process.env.REACT_APP_PROMPT_EXPORT_ENABLED !== "false";

export const PROMPT_PROVIDERS = [
  { key: "chatgpt", label: "ChatGPT", url: "https://chatgpt.com" },
  { key: "claude", label: "Claude", url: "https://claude.ai" },
  { key: "gemini", label: "Gemini", url: "https://gemini.google.com" },
];

export const promptSurface = () =>
  window.innerWidth <= 480 ? "mobile" : "desktop";

const makeUuid = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

// 같은 검색의 재시도가 같은 request_id를 쓰도록 세션에 보관한다 (중복 차감 방지).
export const getPromptRequestId = (logId) => {
  const key = `prompt_export_rid_${logId}`;
  try {
    const saved = sessionStorage.getItem(key);
    if (saved) return saved;
    const created = makeUuid();
    sessionStorage.setItem(key, created);
    return created;
  } catch (e) {
    return makeUuid();
  }
};

// 최초 구매 201 / 기존 구매 재조립 200(cached) / 402 잔액 부족.
export const purchasePromptExport = async (logId) => {
  const response = await apiFetch(
    `${config.serverUri}/private/query/prompt-export/${logId}`,
    {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        request_id: getPromptRequestId(logId),
        surface: promptSurface(),
      }),
    }
  );
  const data = await response.json().catch(() => ({}));
  if ((response.status === 200 || response.status === 201) && data.prompt) {
    data.prompt = buildActionPrompt(data.prompt);
  }
  return { status: response.status, data };
};

// 계측 이벤트 전송. 실패해도 사용자 흐름을 막지 않는다.
export const sendPromptEvent = (payload) => {
  try {
    apiFetch(`${config.serverUri}/private/prompt-events`, {
      method: "POST",
      credentials: "include",
      keepalive: true,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ surface: promptSurface(), ...payload }),
    }).catch(() => {});
  } catch (e) {
    /* noop */
  }
};

// offer_viewed는 계약상 세션당 1회만 집계한다.
export const sendOfferViewedOnce = (logId) => {
  const key = `prompt_export_offer_${logId}`;
  try {
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
  } catch (e) {
    /* 세션 저장 불가 시에도 이벤트는 1회 전송 */
  }
  sendPromptEvent({
    event_name: "ai_prompt_offer_viewed",
    search_log_id: Number(logId),
    result_count: 5,
  });
};

// Clipboard API 실패 시 execCommand 폴백 (iOS Safari 등).
export const copyPromptToClipboard = async (text) => {
  if (typeof text !== "string" || !text.trim()) return false;
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (e) {
    const previousFocus = document.activeElement;
    const textarea = document.createElement("textarea");
    try {
      textarea.value = text;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      return document.execCommand("copy");
    } catch (fallbackError) {
      return false;
    } finally {
      textarea.remove();
      previousFocus?.focus?.({ preventScroll: true });
    }
  }
};
